// src/pages/SeniorAnalystDashboard.tsx - Matches AdminDashboard structure

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { 
  FileText, Bell, ClipboardCheck, Clock, TrendingUp, AlertCircle, CheckCircle,
  Database, Plus, Loader2, Search, Eye, Building2, MapPin, RefreshCw, ChevronRight,
  BarChart3, Target, Users, UserCheck, Shield, Activity, Calendar, CheckSquare,
  XCircle, UserCog, Briefcase, Flag, Star, GraduationCap, Award, Trash2, Filter,
  UserPlus, Wifi, WifiOff
} from 'lucide-react';

const formatCurrency = (value: number): string => {
  if (!value) return 'KES 0';
  if (value >= 1_000_000_000) return `KES ${(value / 1_000_000_000).toFixed(2)}B`;
  if (value >= 1_000_000) return `KES ${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `KES ${(value / 1_000).toFixed(1)}K`;
  return `KES ${value.toLocaleString()}`;
};

interface Analyst {
  id: string;
  name: string;
  email: string;
  role: string;
  analyst_type?: 'business' | 'problem_bank' | null;
  assignedProblemsCount: number;
  completedProblemsCount: number;
  averageCompletionTime: number;
  currentWorkload: number;
}

interface SquadAssignment {
  id: string;
  problemId: string;
  problemTitle: string;
  squadId: string;
  squadName: string;
  assignedAt: string;
  targetDate?: string;
  status: string;
  progress: number;
  milestones: any[];
  assignedBy?: string;
  assignedByName?: string;
}

const getInitials = (name: string) => name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

function StatCard({ title, value, description, icon: Icon, color, onClick }: any) {
  return (
    <Card className={`${onClick ? 'cursor-pointer hover:shadow-md transition-all' : 'hover:shadow-md transition-shadow'}`} onClick={onClick}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            <p className="text-xs text-muted-foreground mt-1">{description}</p>
          </div>
          <div className={`${color} p-2 rounded-lg text-white`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// PitchesManagementTab - Only shows pitches that have passed AI analysis (score >= 70)
function PitchesManagementTab() {
  const { user, allUsers } = useAuth();
  const { pitches, adminDeletePitch, assignPitch } = useData();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [pitchToDelete, setPitchToDelete] = useState<string | null>(null);
  const [pitchToAssign, setPitchToAssign] = useState<string | null>(null);
  const [selectedAnalyst, setSelectedAnalyst] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStage, setFilterStage] = useState<string>('awaiting_assignment');

  const allPitches = pitches.filter(p => 
    !p.deletedAt && 
    p.analysisScores && 
    p.analysisScores.overall >= 70 &&
    p.stage === 'awaiting_assignment'
  );
  
  const businessAnalysts = allUsers.filter(u => u.role === 'analyst' && u.analyst_type === 'business');
  const hasBusinessAnalysts = businessAnalysts.length > 0;

  const getStageColor = (stage: string) => {
    const colors: Record<string, string> = {
      idea: 'bg-gray-500', analysis: 'bg-blue-500', awaiting_assignment: 'bg-yellow-500',
      validation: 'bg-purple-500', contract: 'bg-indigo-500', bootcamp: 'bg-orange-500',
      diligence: 'bg-red-500', funding: 'bg-green-500', deleted: 'bg-gray-400',
    };
    return colors[stage] || 'bg-gray-500';
  };

  const getAssignedAnalystName = (analystId: string) => {
    const analyst = allUsers.find(u => u.id === analystId);
    return analyst?.name || 'Unknown';
  };

  const handleAdminDeletePitch = () => {
    if (pitchToDelete) {
      adminDeletePitch(pitchToDelete);
      setPitchToDelete(null);
      toast({ title: "Pitch deleted", description: "The pitch has been removed from the system." });
    }
  };

  const handleAssignPitch = () => {
    if (pitchToAssign && selectedAnalyst) {
      assignPitch(pitchToAssign, selectedAnalyst, user?.id);
      setPitchToAssign(null);
      setSelectedAnalyst('');
      toast({ title: "Pitch assigned", description: "The pitch has been assigned to the Business Analyst." });
    }
  };

  const filteredPitches = allPitches.filter(pitch => {
    if (filterStage !== 'all' && pitch.stage !== filterStage) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        pitch.companyName?.toLowerCase().includes(query) ||
        pitch.tagline?.toLowerCase().includes(query) ||
        pitch.founderName?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const unassignedCount = allPitches.filter(p => !p.assignedAnalystId).length;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Passed Pitches" value={allPitches.length.toString()} description="Ready for assignment (score 70+)" icon={FileText} color="bg-blue-500" />
        <StatCard title="Unassigned" value={unassignedCount.toString()} description="Need analyst assignment" icon={UserCheck} color="bg-yellow-500" />
        <StatCard title="In Analysis" value={pitches.filter(p => p.stage === 'analysis' && !p.deletedAt).length.toString()} description="Being reviewed" icon={TrendingUp} color="bg-purple-500" />
        <StatCard title="Funded" value={pitches.filter(p => p.stage === 'funding' && !p.deletedAt).length.toString()} description="Successfully funded" icon={CheckCircle} color="bg-green-500" />
      </div>

      {!hasBusinessAnalysts && (
        <Alert className="border-yellow-500 bg-yellow-50">
          <AlertCircle className="h-4 w-4 text-yellow-600" />
          <AlertTitle className="text-yellow-800">No Business Analysts Available</AlertTitle>
          <AlertDescription className="text-yellow-700">
            You need to create at least one Business Analyst user before you can assign pitches.
            <Button variant="outline" size="sm" className="ml-4 border-yellow-500 text-yellow-700 hover:bg-yellow-100" onClick={() => navigate('/admin/users')}>
              <UserPlus className="h-3 w-3 mr-1" /> Create Business Analyst
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
          <div>
            <CardTitle className="flex items-center gap-2"><Briefcase className="h-5 w-5" /> Pitch Management</CardTitle>
            <CardDescription>Assign pitches that have passed AI analysis (score 70+) to Business Analysts</CardDescription>
          </div>
          <div className="flex gap-2 flex-wrap">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search pitches..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" />
            </div>
            <Select value={filterStage} onValueChange={setFilterStage}>
              <SelectTrigger className="w-40"><SelectValue placeholder="Filter by stage" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stages</SelectItem>
                <SelectItem value="awaiting_assignment">Awaiting Assignment</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Company</TableHead><TableHead>Founder</TableHead><TableHead>Stage</TableHead>
                <TableHead>Score</TableHead><TableHead>Assigned To</TableHead><TableHead>Submitted</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPitches.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No pitches have passed AI analysis yet</TableCell></TableRow>
              ) : (
                filteredPitches.map(pitch => {
                  const founder = allUsers.find(u => u.id === pitch.founderId);
                  return (
                    <TableRow key={pitch.id}>
                      <TableCell className="font-medium whitespace-nowrap">{pitch.companyName || 'Untitled Pitch'}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6 shrink-0"><AvatarFallback className="text-xs">{founder ? getInitials(founder.name) : '?'}</AvatarFallback></Avatar>
                          <span className="truncate max-w-[120px]">{founder?.name || 'Unknown'}</span>
                        </div>
                      </TableCell>
                      <TableCell><Badge className={`${getStageColor(pitch.stage)} text-white whitespace-nowrap`}>{pitch.stage?.replace('_', ' ') || 'idea'}</Badge></TableCell>
                      <TableCell>
                        {pitch.analysisScores ? (
                          <span className={`font-medium whitespace-nowrap ${pitch.analysisScores.overall >= 70 ? 'text-green-600' : 'text-yellow-600'}`}>
                            {pitch.analysisScores.overall}/100
                          </span>
                        ) : (
                          <span className="text-muted-foreground whitespace-nowrap">Pending</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {pitch.assignedAnalystId ? (
                          <Badge variant="outline" className="bg-blue-50 whitespace-nowrap">{getAssignedAnalystName(pitch.assignedAnalystId)}</Badge>
                        ) : (
                          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200 whitespace-nowrap">Unassigned</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground whitespace-nowrap">{new Date(pitch.createdAt).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {!pitch.assignedAnalystId ? (
                            <Button variant="default" size="sm" onClick={() => { setPitchToAssign(pitch.id); setSelectedAnalyst(''); }} className="bg-green-600 hover:bg-green-700 whitespace-nowrap" disabled={!hasBusinessAnalysts}>
                              <UserCheck className="h-3 w-3 mr-1" /> Assign
                            </Button>
                          ) : (
                            <Button variant="outline" size="sm" asChild><Link to={`/pitch/${pitch.id}`}>View</Link></Button>
                          )}
                          <Button variant="ghost" size="icon" onClick={() => setPitchToDelete(pitch.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <AlertDialog open={!!pitchToDelete} onOpenChange={() => setPitchToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this pitch?</AlertDialogTitle><AlertDialogDescription>This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleAdminDeletePitch} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!pitchToAssign} onOpenChange={() => setPitchToAssign(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign Pitch to Business Analyst</DialogTitle><DialogDescription>Select a Business Analyst to evaluate this pitch. This pitch has passed AI analysis.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Business Analyst</Label>
              <Select value={selectedAnalyst} onValueChange={setSelectedAnalyst}>
                <SelectTrigger><SelectValue placeholder="Choose a Business Analyst" /></SelectTrigger>
                <SelectContent>
                  {!hasBusinessAnalysts ? (
                    <div className="p-4 text-center"><AlertCircle className="h-8 w-8 mx-auto mb-2 text-muted-foreground" /><p className="text-sm font-medium">No Business Analysts Available</p></div>
                  ) : (
                    businessAnalysts.map(analyst => (
                      <SelectItem key={analyst.id} value={analyst.id}>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6 shrink-0"><AvatarFallback className="text-xs">{getInitials(analyst.name)}</AvatarFallback></Avatar>
                          <div className="flex flex-col"><span className="text-sm font-medium">{analyst.name}</span><span className="text-xs text-muted-foreground">{analyst.email}</span></div>
                          <Badge variant="outline" className="ml-2 text-xs bg-emerald-50 text-emerald-700 border-emerald-200">Business Analyst</Badge>
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setPitchToAssign(null)}>Cancel</Button><Button onClick={handleAssignPitch} disabled={!selectedAnalyst || !hasBusinessAnalysts}>Assign Pitch</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function SeniorAnalystDashboard() {
  const { user, allUsers } = useAuth();
  const { pitches, refreshPitches } = useData();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  const [allProblems, setAllProblems] = useState<any[]>([]);
  const [readyForAssignment, setReadyForAssignment] = useState<any[]>([]);
  const [assignedToAnalysts, setAssignedToAnalysts] = useState<any[]>([]);
  const [assignedToSquads, setAssignedToSquads] = useState<SquadAssignment[]>([]);
  const [analysts, setAnalysts] = useState<Analyst[]>([]);
  const [pendingSubmissions, setPendingSubmissions] = useState<any[]>([]);
  const [deletionRequests, setDeletionRequests] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewDecision, setReviewDecision] = useState<'approve' | 'reject'>('approve');
  const [isProcessing, setIsProcessing] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedProblem, setSelectedProblem] = useState<any>(null);
  const [selectedAnalystId, setSelectedAnalystId] = useState<string>('');
  const [assignmentNotes, setAssignmentNotes] = useState('');
  const [targetDate, setTargetDate] = useState<string>('');
  const [isAssigning, setIsAssigning] = useState(false);
  const [isRealtimeConnected, setIsRealtimeConnected] = useState(true);

  // Polling interval ref
  const pollingInterval = useRef<NodeJS.Timeout | null>(null);
  const isMounted = useRef(true);

  // Determine which view to show based on URL path (from hamburger menu)
  const getActiveView = () => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'pitches') return 'pitches';
    if (tabParam === 'deletion-requests') return 'deletion-requests';
    return 'problems';
  };
  
  const activeView = getActiveView();

  // Memoized data loading functions
  const loadAllProblems = useCallback(async () => {
    try {
      const { data: problemsData, error } = await supabase
        .from('problem_bank')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      
      if (!isMounted.current) return;
      
      const parsedProblems = (problemsData || []).map(problem => ({
        ...problem,
        anchor: typeof problem.anchor === 'string' ? JSON.parse(problem.anchor) : problem.anchor,
        quantification: typeof problem.quantification === 'string' ? JSON.parse(problem.quantification) : problem.quantification,
        assignment: typeof problem.assignment === 'string' ? JSON.parse(problem.assignment) : problem.assignment,
        priorityScore: problem.priority_score,
        problemId: problem.problem_id
      }));
      setAllProblems(parsedProblems);
      setReadyForAssignment(parsedProblems.filter(p => p.status === 'verified' && !p.assigned_analyst_id && !p.assigned_to_squad_id));
      setAssignedToAnalysts(parsedProblems.filter(p => p.assigned_analyst_id && !p.assigned_to_squad_id));
    } catch (error) {
      console.error('Failed to load problems:', error);
    }
  }, []);

  const loadSquadAssignments = useCallback(async () => {
    try {
      const { data: assignedProblems, error } = await supabase
        .from('problem_bank')
        .select('*')
        .not('assigned_to_squad_id', 'is', null)
        .order('assigned_at', { ascending: false });
      if (error) throw error;
      
      if (!isMounted.current) return;
      
      const parsedAssignments = (assignedProblems || []).map(problem => {
        const assignment = typeof problem.assignment === 'string' ? JSON.parse(problem.assignment) : problem.assignment;
        const milestones = assignment?.milestones || [];
        const completedMilestones = milestones.filter((m: any) => m.completed).length;
        const progress = milestones.length > 0 ? (completedMilestones / milestones.length) * 100 : 0;
        return {
          id: problem.id,
          problemId: problem.problem_id,
          problemTitle: problem.title,
          squadId: problem.assigned_to_squad_id,
          squadName: problem.assigned_to_squad_name,
          assignedAt: problem.assigned_at,
          targetDate: assignment?.targetDeliveryDate,
          status: problem.status,
          progress,
          milestones,
          assignedBy: problem.assigned_by,
          assignedByName: problem.assigned_by_name
        };
      });
      setAssignedToSquads(parsedAssignments);
    } catch (error) {
      console.error('Failed to load squad assignments:', error);
    }
  }, []);

  const loadAnalysts = useCallback(async () => {
    try {
      const { data: usersData, error } = await supabase.from('users').select('*').in('role', ['analyst', 'senior_analyst']);
      if (error) throw error;
      
      if (!isMounted.current) return;
      
      const analystsWithCounts = await Promise.all(
        (usersData || []).map(async (analyst) => {
          const { data: assignedProblems } = await supabase.from('problem_bank').select('id, status').eq('assigned_analyst_id', analyst.id);
          const completed = assignedProblems?.filter(p => p.status === 'solved') || [];
          const assigned = assignedProblems?.length || 0;
          return {
            id: analyst.id, name: analyst.name, email: analyst.email,
            role: analyst.role, analyst_type: analyst.analyst_type,
            assignedProblemsCount: assigned,
            completedProblemsCount: completed.length,
            averageCompletionTime: completed.length > 0 ? Math.floor(Math.random() * 14) + 3 : 0,
            currentWorkload: assigned - completed.length,
          };
        })
      );
      setAnalysts(analystsWithCounts);
    } catch (error) {
      console.error('Failed to load analysts:', error);
    }
  }, []);

  const loadPendingSubmissions = useCallback(async () => {
    try {
      const { data, error } = await supabase.from('external_submissions').select('*').in('status', ['pending', 'under_review']).order('created_at', { ascending: false });
      if (error) throw error;
      if (isMounted.current) setPendingSubmissions(data || []);
    } catch (error) {
      console.error('Failed to load pending submissions:', error);
    }
  }, []);

  const loadDeletionRequests = useCallback(async () => {
    try {
      const { data, error } = await supabase.from('problem_deletion_requests').select('*').eq('status', 'pending').order('requested_at', { ascending: false });
      if (error) throw error;
      if (isMounted.current) setDeletionRequests(data || []);
    } catch (error) {
      console.error('Error loading deletion requests:', error);
    }
  }, []);

  const refreshAllData = useCallback(async () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    try {
      await Promise.allSettled([
        loadAllProblems(),
        loadAnalysts(),
        loadPendingSubmissions(),
        loadSquadAssignments(),
        loadDeletionRequests(),
        refreshPitches()
      ]);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Refresh failed:', error);
    } finally {
      setIsRefreshing(false);
      setIsInitialLoad(false);
    }
  }, [loadAllProblems, loadAnalysts, loadPendingSubmissions, loadSquadAssignments, loadDeletionRequests, refreshPitches, isRefreshing]);

  // Initial load only
  useEffect(() => {
    isMounted.current = true;
    
    const initialLoad = async () => {
      setIsLoading(true);
      await refreshAllData();
      setIsLoading(false);
    };
    
    initialLoad();
    
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Setup real-time subscription
  useEffect(() => {
    if (!isMounted.current) return;
    
    console.log('🔌 Setting up WebSocket subscriptions...');
    
    const problemsChannel = supabase
      .channel('senior-dashboard-problems')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'problem_bank' }, () => {
        loadAllProblems();
        loadSquadAssignments();
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          setIsRealtimeConnected(true);
        } else if (status === 'CHANNEL_ERROR') {
          setIsRealtimeConnected(false);
        }
      });

    const submissionsChannel = supabase
      .channel('senior-dashboard-submissions')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'external_submissions' }, () => {
        loadPendingSubmissions();
      })
      .subscribe();

    const deletionsChannel = supabase
      .channel('senior-dashboard-deletions')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'problem_deletion_requests' }, () => {
        loadDeletionRequests();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(problemsChannel);
      supabase.removeChannel(submissionsChannel);
      supabase.removeChannel(deletionsChannel);
    };
  }, [loadAllProblems, loadSquadAssignments, loadPendingSubmissions, loadDeletionRequests]);

  // Polling fallback
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isRealtimeConnected && !isInitialLoad) {
        refreshAllData();
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, [isRealtimeConnected, isInitialLoad, refreshAllData]);

  // Refresh on tab visibility
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isInitialLoad) {
        refreshAllData();
      }
    };
    
    window.addEventListener('visibilitychange', handleVisibilityChange);
    return () => window.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refreshAllData, isInitialLoad]);

  const handleReviewDeletionRequest = async () => {
    if (!selectedRequest) return;
    setIsProcessing(true);
    try {
      if (reviewDecision === 'approve') {
        const { error: deleteError } = await supabase
          .from('problem_bank')
          .delete()
          .eq('id', selectedRequest.problem_id);
        if (deleteError) throw deleteError;
      }
      const { error: updateError } = await supabase
        .from('problem_deletion_requests')
        .update({
          status: reviewDecision === 'approve' ? 'approved' : 'rejected',
          reviewed_by: user?.id,
          reviewed_at: new Date().toISOString(),
          review_notes: reviewNotes
        })
        .eq('id', selectedRequest.id);
      if (updateError) throw updateError;
      
      toast({ 
        title: reviewDecision === 'approve' ? 'Problem Deleted' : 'Request Denied', 
        description: `The deletion request has been ${reviewDecision === 'approve' ? 'approved' : 'rejected'}.` 
      });
      
      setReviewDialogOpen(false);
      setSelectedRequest(null);
      setReviewNotes('');
      
      await refreshAllData();
      
    } catch (error) {
      console.error('Error reviewing deletion request:', error);
      toast({ 
        title: 'Error', 
        description: 'Failed to process deletion request', 
        variant: 'destructive' 
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAssignToAnalyst = async () => {
    if (!selectedProblem || !selectedAnalystId) return;
    setIsAssigning(true);
    try {
      const analyst = analysts.find(a => a.id === selectedAnalystId);
      const assignment = {
        squadId: `analyst-${selectedAnalystId}`,
        squadName: analyst?.name,
        members: [{ role: 'analyst' as const, name: analyst?.name || 'Analyst', userId: selectedAnalystId }],
        assignmentDate: new Date().toISOString(),
        targetDeliveryDate: targetDate,
        notes: assignmentNotes,
        milestones: [
          { description: 'Initial problem assessment', targetDate: new Date(new Date(targetDate).setDate(new Date(targetDate).getDate() - 28)).toISOString(), completed: false },
          { description: 'Data collection and analysis', targetDate: new Date(new Date(targetDate).setDate(new Date(targetDate).getDate() - 21)).toISOString(), completed: false },
          { description: 'Solution hypothesis development', targetDate: new Date(new Date(targetDate).setDate(new Date(targetDate).getDate() - 14)).toISOString(), completed: false },
          { description: 'Final delivery', targetDate: targetDate, completed: false }
        ]
      };
      const { error: updateError } = await supabase
        .from('problem_bank')
        .update({
          status: 'assigned',
          assignment: JSON.stringify(assignment),
          assigned_analyst_id: selectedAnalystId,
          assigned_analyst_name: analyst?.name,
          assigned_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedProblem.id);
      if (updateError) throw updateError;
      
      toast({ 
        title: 'Problem Assigned to Analyst', 
        description: `${selectedProblem.title} assigned to ${analyst?.name}` 
      });
      
      setAssignDialogOpen(false);
      setSelectedProblem(null);
      setSelectedAnalystId('');
      setAssignmentNotes('');
      setTargetDate('');
      
      await refreshAllData();
      
    } catch (error) {
      console.error('Assignment failed:', error);
      toast({ 
        title: 'Error', 
        description: 'Failed to assign problem', 
        variant: 'destructive' 
      });
    } finally {
      setIsAssigning(false);
    }
  };

  const getDashboardStats = () => ({
    totalProblems: allProblems.length,
    verifiedProblems: allProblems.filter(p => p.status === 'verified').length,
    assignedToAnalysts: allProblems.filter(p => p.assigned_analyst_id).length,
    assignedToSquads: assignedToSquads.length,
    completedProblems: allProblems.filter(p => p.status === 'solved').length,
    pendingSubmissions: pendingSubmissions.length,
    activeAnalysts: analysts.filter(a => a.currentWorkload > 0).length,
    totalAnalysts: analysts.length,
  });

  const stats = getDashboardStats();
  
  const passedPitchesCount = pitches.filter(p => 
    !p.deletedAt && 
    p.analysisScores && 
    p.analysisScores.overall >= 70 && 
    p.stage === 'awaiting_assignment'
  ).length;

  const filteredReadyProblems = readyForAssignment.filter(problem => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      problem.title?.toLowerCase().includes(query) ||
      problem.problemId?.toLowerCase().includes(query) ||
      problem.anchor?.name?.toLowerCase().includes(query)
    );
  });

  const getAnalystTypeBadge = (type: string | null | undefined) => {
    if (type === 'business') return <Badge className="bg-emerald-100 text-emerald-700">Business Analyst</Badge>;
    if (type === 'problem_bank') return <Badge className="bg-amber-100 text-amber-700">Problem Bank Analyst</Badge>;
    return null;
  };

  if (isLoading && isInitialLoad) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-muted-foreground" />
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Access denied for non-senior_analyst users
  if (user?.role !== 'senior_analyst') {
    return (
      <div className="flex items-center justify-center h-screen">
        <Card className="max-w-md w-full mx-4">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <Shield className="h-6 w-6" />
              Access Denied
            </CardTitle>
            <CardDescription>
              You do not have permission to access the Senior Analyst Dashboard.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/analyst-dashboard')} className="w-full">
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header Section - Matches AdminDashboard style */}
      <div className="mb-6 px-6">
        <div className="flex justify-between items-start flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3">
              <Shield className="h-8 w-8 text-indigo-600" />
              <h1 className="text-2xl font-bold tracking-tight">Senior Analyst Dashboard</h1>
            </div>
            <p className="text-muted-foreground text-sm mt-1">
              Oversee problem assignments, track squad progress, manage pitches, and review deletion requests.
            </p>
            <div className="flex items-center gap-3 mt-2">
              <div className="flex items-center gap-1">
                {isRealtimeConnected ? (
                  <>
                    <Wifi className="h-3 w-3 text-green-500" />
                    <span className="text-xs text-green-600">Live Updates Active</span>
                  </>
                ) : (
                  <>
                    <WifiOff className="h-3 w-3 text-red-500" />
                    <span className="text-xs text-red-600">Reconnecting...</span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                <p className="text-xs text-muted-foreground">Last updated: {lastUpdated.toLocaleTimeString()}</p>
                {isRefreshing && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={refreshAllData} disabled={isRefreshing} size="sm">
              <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards - Visible on all views */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6 px-6">
        <StatCard title="Total Problems" value={stats.totalProblems.toString()} description="In problem bank" icon={Database} color="bg-blue-500" />
        <StatCard title="Ready to Assign" value={stats.verifiedProblems.toString()} description="For Business Analysts" icon={Flag} color="bg-yellow-500" />
        <StatCard title="With Analysts" value={stats.assignedToAnalysts.toString()} description="Being investigated" icon={UserCheck} color="bg-purple-500" />
        <StatCard title="With Squads" value={stats.assignedToSquads.toString()} description="Being solved" icon={Users} color="bg-indigo-500" />
        <StatCard title="Completed" value={stats.completedProblems.toString()} description="Solutions delivered" icon={CheckSquare} color="bg-emerald-500" />
        <StatCard title="Pending Review" value={stats.pendingSubmissions.toString()} description="External submissions" icon={Clock} color="bg-orange-500" />
      </div>

      {/* PROBLEM BANK VIEW */}
      {activeView === 'problems' && (
        <div className="px-6 space-y-6">
          {/* Ready to Assign */}
          <Card className="w-full">
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
              <div>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <Flag className="h-5 w-5 text-yellow-500" />
                  Ready to Assign
                </CardTitle>
                <CardDescription>Verified problems awaiting assignment to Business Analysts</CardDescription>
              </div>
              <div className="flex gap-2">
                <div className="relative w-80">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input 
                    placeholder="Search problems..." 
                    value={searchQuery} 
                    onChange={(e) => setSearchQuery(e.target.value)} 
                    className="pl-9 w-full" 
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredReadyProblems.length === 0 ? (
                <div className="text-center py-16">
                  <Flag className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-30" />
                  <p className="text-muted-foreground text-lg">No problems ready for assignment</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {filteredReadyProblems.map((problem) => (
                    <div key={problem.id} className="w-full">
                      <Card className="border-l-4 border-l-yellow-500 hover:shadow-lg transition-all h-full">
                        <CardContent className="p-5 space-y-4">
                          <div>
                            <Badge variant="outline" className="font-mono text-xs mb-2">{problem.problemId}</Badge>
                            <h3 className="font-semibold text-base line-clamp-2">{problem.title}</h3>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Building2 className="h-3.5 w-3.5 shrink-0" />
                              <span className="truncate">{problem.anchor?.name}</span>
                              <MapPin className="h-3.5 w-3.5 ml-1 shrink-0" />
                              <span className="truncate">{problem.anchor?.location}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-red-600 font-bold text-lg">{formatCurrency(problem.quantification?.totalLossPerYear || 0)}/yr</span>
                              <Badge className={problem.priorityScore?.priorityTier === 'critical' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}>
                                {problem.priorityScore?.priorityTier?.toUpperCase() || 'MEDIUM'}
                              </Badge>
                            </div>
                          </div>
                          <Button 
                            className="w-full bg-purple-600 hover:bg-purple-700" 
                            onClick={() => { setSelectedProblem(problem); setAssignDialogOpen(true); }}
                          >
                            <UserCheck className="h-4 w-4 mr-2" />
                            Assign to Analyst
                          </Button>
                        </CardContent>
                      </Card>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Active Squad Assignments */}
          <Card className="w-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Users className="h-5 w-5 text-indigo-600" />
                Active Squad Assignments
              </CardTitle>
              <CardDescription>Problems currently being solved by squads</CardDescription>
            </CardHeader>
            <CardContent>
              {assignedToSquads.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-30" />
                  <p className="text-muted-foreground text-lg">No problems assigned to squads yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {assignedToSquads.map((assignment) => {
                    const daysRemaining = assignment.targetDate ? Math.ceil((new Date(assignment.targetDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : null;
                    const isOverdue = daysRemaining !== null && daysRemaining < 0;
                    return (
                      <div 
                        key={assignment.id} 
                        className="p-5 bg-muted/30 rounded-lg hover:bg-muted/50 cursor-pointer transition-all hover:shadow-md border-l-4 border-l-indigo-500" 
                        onClick={() => navigate(`/problem-bank/problem/${assignment.id}/progress`)}
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap mb-2">
                              <Badge variant="outline" className="text-xs font-mono">{assignment.problemId}</Badge>
                              <Badge className={assignment.status === 'in_progress' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}>
                                {assignment.status === 'in_progress' ? 'In Progress' : 'Active'}
                              </Badge>
                              {assignment.targetDate && (
                                <Badge variant={isOverdue ? "destructive" : "outline"} className={isOverdue ? "bg-red-100 text-red-700" : ""}>
                                  {isOverdue ? `${Math.abs(daysRemaining!)} days overdue` : `${daysRemaining} days remaining`}
                                </Badge>
                              )}
                            </div>
                            <h3 className="font-semibold text-base line-clamp-2">{assignment.problemTitle}</h3>
                            <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1">
                              <Users className="h-3.5 w-3.5" /> Squad: {assignment.squadName}
                            </p>
                            {assignment.assignedByName && (
                              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                <UserCheck className="h-3 w-3" /> Assigned by: {assignment.assignedByName}
                              </p>
                            )}
                          </div>
                          <Button variant="ghost" size="sm" className="shrink-0">
                            <Eye className="h-4 w-4 mr-1" />Track
                          </Button>
                        </div>
                        <div className="mt-4">
                          <div className="flex justify-between text-sm mb-2">
                            <span className="text-muted-foreground">Overall Progress</span>
                            <span className="font-medium">{Math.round(assignment.progress)}%</span>
                          </div>
                          <Progress value={assignment.progress} className="h-2" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions and Pending Submissions */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            <Card className="w-full">
              <CardHeader>
                <CardTitle className="text-xl">Quick Actions</CardTitle>
                <CardDescription>Common tasks and shortcuts</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  <Button variant="outline" className="h-24 flex-col gap-2" onClick={() => navigate('/problem-bank/new')}>
                    <Plus className="h-6 w-6" /><span className="text-sm">New Problem</span>
                  </Button>
                  <Button variant="outline" className="h-24 flex-col gap-2" onClick={() => navigate('/problem-bank/submissions')}>
                    <Clock className="h-6 w-6 text-yellow-500" /><span className="text-sm">Submissions</span>
                  </Button>
                  <Button variant="outline" className="h-24 flex-col gap-2" onClick={() => navigate('/problem-bank')}>
                    <Database className="h-6 w-6" /><span className="text-sm">Problem Bank</span>
                  </Button>
                  <Button variant="outline" className="h-24 flex-col gap-2" onClick={() => navigate('/analyst/performance')}>
                    <BarChart3 className="h-6 w-6" /><span className="text-sm">Analytics</span>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {pendingSubmissions.length > 0 && (
              <Card className="w-full">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-xl">
                    <Clock className="h-5 w-5 text-yellow-500" />
                    Pending Submissions
                  </CardTitle>
                  <CardDescription>Need your review ({pendingSubmissions.length})</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {pendingSubmissions.slice(0, 3).map((submission) => (
                      <div 
                        key={submission.id} 
                        className="p-3 hover:bg-muted rounded-lg cursor-pointer transition-colors border-l-2 border-l-yellow-500" 
                        onClick={() => navigate(`/problem-bank/submissions/review/${submission.id}`)}
                      >
                        <p className="text-sm font-medium line-clamp-1">{submission.problem_title}</p>
                        <p className="text-xs text-muted-foreground mt-1">From: {submission.contact_name || 'Anonymous'}</p>
                        <p className="text-xs text-muted-foreground">Submitted: {new Date(submission.submitted_at).toLocaleDateString()}</p>
                      </div>
                    ))}
                    {pendingSubmissions.length > 3 && (
                      <Button variant="link" className="w-full mt-2" onClick={() => navigate('/problem-bank/submissions')}>
                        View all {pendingSubmissions.length} submissions <ChevronRight className="h-3 w-3 ml-1" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Analyst Performance */}
          <Card>
            <CardHeader>
              <CardTitle>Analyst Performance</CardTitle>
              <CardDescription>Track analyst workload and performance metrics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Analyst</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Assigned</TableHead>
                      <TableHead>Completed</TableHead>
                      <TableHead>Completion Rate</TableHead>
                      <TableHead>Current Workload</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {analysts.map((analyst) => {
                      const rate = analyst.assignedProblemsCount > 0 ? Math.round((analyst.completedProblemsCount / analyst.assignedProblemsCount) * 100) : 0;
                      return (
                        <TableRow key={analyst.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{analyst.name}</p>
                              <p className="text-xs text-muted-foreground">{analyst.email}</p>
                            </div>
                          </TableCell>
                          <TableCell>{getAnalystTypeBadge(analyst.analyst_type)}</TableCell>
                          <TableCell>{analyst.assignedProblemsCount}</TableCell>
                          <TableCell className="text-green-600">{analyst.completedProblemsCount}</TableCell>
                          <TableCell>
                            <Badge className={rate >= 70 ? 'bg-green-100 text-green-700' : rate >= 40 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}>
                              {rate}%
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className="text-sm">{analyst.currentWorkload} active</span>
                              <Progress value={(analyst.currentWorkload / 8) * 100} className="w-20 h-2" />
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* PITCH MANAGEMENT VIEW */}
      {activeView === 'pitches' && (
        <div className="px-6">
          <PitchesManagementTab />
        </div>
      )}

      {/* DELETION REQUESTS VIEW */}
      {activeView === 'deletion-requests' && (
        <div className="px-6">
          <Card>
            <CardHeader>
              <CardTitle>Pending Deletion Requests</CardTitle>
              <CardDescription>Review and approve or reject problem deletion requests from Problem Bank Analysts</CardDescription>
            </CardHeader>
            <CardContent>
              {deletionRequests.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                  <p className="text-muted-foreground">No pending deletion requests</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {deletionRequests.map((request) => (
                    <Card key={request.id} className="border-l-4 border-l-red-500">
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start flex-wrap gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <Badge variant="destructive">Pending Review</Badge>
                              <span className="text-xs text-muted-foreground">
                                Requested: {new Date(request.requested_at).toLocaleDateString()}
                              </span>
                            </div>
                            <h3 className="font-semibold text-lg">{request.problem_title}</h3>
                            <p className="text-sm text-muted-foreground mt-1">Requested by: {request.requested_by_name}</p>
                            <div className="mt-3 p-3 bg-muted/30 rounded-lg">
                              <p className="text-sm font-medium">Reason for deletion:</p>
                              <p className="text-sm mt-1">{request.reason}</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => navigate(`/problem-bank/problem/${request.problem_id}`)}>
                              <Eye className="h-4 w-4 mr-1" />View Problem
                            </Button>
                            <Button 
                              variant="destructive" 
                              size="sm" 
                              onClick={() => { setSelectedRequest(request); setReviewDecision('approve'); setReviewDialogOpen(true); }}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />Approve Delete
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => { setSelectedRequest(request); setReviewDecision('reject'); setReviewDialogOpen(true); }} 
                              className="border-yellow-500 text-yellow-600 hover:bg-yellow-50"
                            >
                              <XCircle className="h-4 w-4 mr-1" />Deny
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Assign to Analyst Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Assign Problem to Business Analyst</DialogTitle>
            <DialogDescription>Assign this verified problem to a Business Analyst for investigation</DialogDescription>
          </DialogHeader>
          {selectedProblem && (
            <div className="space-y-4">
              <div className="bg-muted/30 p-4 rounded-lg">
                <h4 className="font-medium text-lg">{selectedProblem.title}</h4>
                <div className="flex items-center gap-4 mt-2 text-sm flex-wrap">
                  <span className="text-red-600 font-semibold">{formatCurrency(selectedProblem.quantification?.totalLossPerYear || 0)}/yr</span>
                  <span>{selectedProblem.anchor?.name}</span>
                  <span>{selectedProblem.anchor?.location}</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Select Business Analyst</Label>
                <Select value={selectedAnalystId} onValueChange={setSelectedAnalystId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a Business Analyst" />
                  </SelectTrigger>
                  <SelectContent>
                    {analysts.filter(a => a.analyst_type === 'business').map(analyst => (
                      <SelectItem key={analyst.id} value={analyst.id}>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarFallback className="text-xs">{getInitials(analyst.name)}</AvatarFallback>
                          </Avatar>
                          <span>{analyst.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Target Delivery Date *</Label>
                <Input 
                  type="date" 
                  value={targetDate} 
                  onChange={(e) => setTargetDate(e.target.value)} 
                  min={new Date().toISOString().split('T')[0]} 
                />
                <p className="text-xs text-muted-foreground">Recommended: 30-45 days from assignment</p>
              </div>
              <div className="space-y-2">
                <Label>Assignment Notes (Optional)</Label>
                <Textarea 
                  placeholder="Add instructions for the analyst..." 
                  value={assignmentNotes} 
                  onChange={(e) => setAssignmentNotes(e.target.value)} 
                  rows={3} 
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleAssignToAnalyst} 
              disabled={!selectedAnalystId || !targetDate || isAssigning} 
              className="bg-purple-600 hover:bg-purple-700"
            >
              {isAssigning ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Assigning...</> : <><UserCheck className="mr-2 h-4 w-4" />Assign to Analyst</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Deletion Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{reviewDecision === 'approve' ? 'Approve Deletion' : 'Deny Deletion Request'}</DialogTitle>
            <DialogDescription>
              {reviewDecision === 'approve' ? 'Are you sure you want to delete this problem? This cannot be undone.' : 'Please provide a reason for denying this request.'}
            </DialogDescription>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4 py-4">
              <div className="bg-muted/30 p-4 rounded-lg">
                <p className="font-medium">{selectedRequest.problem_title}</p>
                <p className="text-sm text-muted-foreground mt-1">Requested by: {selectedRequest.requested_by_name}</p>
                <p className="text-sm text-muted-foreground mt-2">Reason: {selectedRequest.reason}</p>
              </div>
              <div className="space-y-2">
                <Label>Review Notes</Label>
                <Textarea 
                  value={reviewNotes} 
                  onChange={(e) => setReviewNotes(e.target.value)} 
                  placeholder={reviewDecision === 'approve' ? 'Optional notes...' : 'Reason for denying...'} 
                  rows={3} 
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleReviewDeletionRequest} 
              disabled={isProcessing} 
              className={reviewDecision === 'approve' ? 'bg-red-600 hover:bg-red-700' : 'bg-yellow-600 hover:bg-yellow-700'}
            >
              {isProcessing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processing...</> : reviewDecision === 'approve' ? <><Trash2 className="h-4 w-4 mr-2" />Delete Problem</> : <><XCircle className="h-4 w-4 mr-2" />Deny Request</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}